import { z } from 'zod';
import { jdContactSchema } from './jd-content.schema';

/** Candidate Profile content (spec §7.2, template 2026). */

export const profileFactSchema = z.object({
  label: z.string().min(1).max(60),
  value: z.string().min(1).max(220),
});

/**
 * The six fact cards of the 2026 template, in this order. Versions written before the template
 * change carry the former four (Experience · Location · Languages · Availability) and stay valid.
 */
export const PROFILE_FACT_LABELS_EN = [
  'Role',
  'Experience',
  'Core stack',
  'Languages',
  'Work model',
  'Availability',
] as const;
export const PROFILE_FACT_LABELS_DE = [
  'Rolle',
  'Erfahrung',
  'Kern-Stack',
  'Sprachen',
  'Arbeitsmodell',
  'Verfügbarkeit',
] as const;
export const PROFILE_FACTS_MIN = 4;
export const PROFILE_FACTS_MAX = 6;

/** Index of the Languages fact (by label; falls back to the position of each layout). */
export function profileLanguagesFactIndex(facts: ReadonlyArray<{ label: string }>): number {
  const byLabel = facts.findIndex((f) => /^(languages|sprachen)$/i.test(f.label.trim()));
  if (byLabel >= 0) return byLabel;
  return facts.length >= 6 ? 3 : 2;
}

export const profileProjectSchema = z.object({
  role: z.string().min(1).max(120),
  client: z.string().max(160).optional(),
  period: z.string().max(60).optional(),
  context: z.string().max(600).optional(),
  bullets: z.array(z.string().min(1)).max(6).optional(),
  stack: z.string().max(400).optional(),
});

export const profileContentSchema = z
  .object({
    lang: z.enum(['en', 'de']).default('en'),
    ref: z.string().regex(/^DC-C-\d{4}-\d{4,}$/, 'ref must look like DC-C-YYYY-NNNN'),
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'date must be YYYY-MM-DD'),
    /** Kept for the content checks (the client must not be named); no longer printed. */
    preparedFor: z.string().max(120).optional(),
    /** "Proposed for: <position title>, request <no>" under the title. */
    proposedFor: z.string().max(200).optional(),
    candidate: z.object({
      displayName: z.string().min(1).max(80),
      title: z.string().min(1).max(120),
      headline: z.string().max(400).optional(),
      facts: z
        .array(profileFactSchema)
        .min(
          PROFILE_FACTS_MIN,
          `candidate.facts must contain ${PROFILE_FACTS_MIN} to ${PROFILE_FACTS_MAX} entries`,
        )
        .max(
          PROFILE_FACTS_MAX,
          `candidate.facts must contain ${PROFILE_FACTS_MIN} to ${PROFILE_FACTS_MAX} entries`,
        ),
    }),
    assessment: z
      .object({
        title: z.string().max(80).optional(),
        intro: z.string().max(600).optional(),
        /** "Label: text" bullets are rendered with the label in bold. */
        bullets: z.array(z.string().min(1)).max(6).optional(),
        signoff: z.string().max(300).optional(),
      })
      .optional(),
    summary: z.union([z.string(), z.array(z.string().min(1))]).optional(),
    skills: z
      .array(z.tuple([z.string(), z.string()]))
      .max(10)
      .optional(),
    projects: z.array(profileProjectSchema).max(8).optional(),
    education: z
      .array(z.object({ title: z.string(), institution: z.string().optional(), year: z.string().optional() }))
      .optional(),
    certifications: z
      .array(z.object({ title: z.string(), issuer: z.string().optional(), year: z.string().optional() }))
      .optional(),
    /** "English C1 · German B1 · Romanian native"; defaults to the Languages fact. */
    languages: z.string().max(300).optional(),
    nextStepText: z.string().max(600).optional(),
    contact: jdContactSchema.optional(),
  })
  .strict();

export type ProfileContent = z.infer<typeof profileContentSchema>;
export type ProfileContentInput = z.input<typeof profileContentSchema>;
